if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.mql = window.matchMedia('(min-width: 750px)');
        this.variantChangeUnsubscriber = undefined;
        this._eventsBound = false;
      }

      get elements() {
        return {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
      }

      connectedCallback() {
        this.bindEvents();
        this.initGalleryViewerHeight();
        this.scheduleThumbnailSliderRefresh();
        this.updateThumbnailArrowsVisibility();
      }

      disconnectedCallback() {
        this._viewerResizeObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
      }

      initGalleryViewerHeight() {
        const viewer = this.elements.viewer;
        if (!viewer || this._viewerResizeObserver) return;

        this.updateGalleryViewerHeight();
        this._viewerResizeObserver = new ResizeObserver(() => this.updateGalleryViewerHeight());
        this._viewerResizeObserver.observe(viewer);

        viewer.querySelectorAll('.product__media-item img').forEach((img) => {
          if (img.complete) return;
          img.addEventListener('load', () => this.updateGalleryViewerHeight(), { once: true });
          img.addEventListener('error', () => this.updateGalleryViewerHeight(), { once: true });
        });
      }

      updateGalleryViewerHeight() {
        const viewer = this.elements.viewer;
        if (!viewer) return;

        const height = Math.round(viewer.getBoundingClientRect().height);
        const value = `${height}px`;

        this.style.setProperty('--kg-gallery-viewer-height', value);
        viewer.style.setProperty('--kg-gallery-viewer-height', value);
        this.elements.thumbnails?.querySelector('.thumbnail-list')?.style.setProperty('--kg-gallery-viewer-height', value);
      }

      bindEvents() {
        if (this._eventsBound) return;
        this._eventsBound = true;

        const { viewer, thumbnails } = this.elements;

        if (viewer) {
          viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (thumbnails) {
          thumbnails.addEventListener('click', this.onThumbnailClick.bind(this));
          if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }
      }

      onThumbnailClick(event) {
        const thumbnailItem = event.target.closest('.thumbnail-list__item[data-target]');
        if (!thumbnailItem || thumbnailItem.classList.contains('hidden')) return;

        const button = event.target.closest('button.thumbnail');
        if (!button || !thumbnailItem.contains(button)) return;

        this.setActiveMedia(thumbnailItem.dataset.target, false);
      }

      mediaAltMatchesVariant(altText, variant) {
        if (!variant) return false;

        const alt = (altText || '').toLowerCase().trim();
        if (!alt) return false;

        const title = (variant.title || '').toLowerCase().trim();
        if (alt === title) return true;

        const options = [variant.option1, variant.option2, variant.option3]
          .filter(Boolean)
          .map((value) => value.toLowerCase().trim());

        const segments = alt
          .split(/[,|]/)
          .map((segment) => segment.trim())
          .filter(Boolean);

        const valuesToMatch = segments.length ? segments : [alt];

        return valuesToMatch.some((segment) => options.includes(segment));
      }

      filterMediaByVariant(variant) {
        if (!variant || this.dataset.filterByAlt !== 'true') return;

        const featuredMediaId = variant.featured_media?.id?.toString();

        const toggleItem = (item, matches) => {
          item.classList.toggle('hidden', !matches);
          if (matches) {
            item.removeAttribute('hidden');
          } else {
            item.setAttribute('hidden', '');
          }
        };

        const viewerItems = this.elements.viewer?.querySelectorAll('li[data-media-id]') || [];

        viewerItems.forEach((item) => {
          const mediaId = item.dataset.mediaId.split('-').pop();
          const isFeatured = featuredMediaId && mediaId === featuredMediaId;
          const matches = isFeatured || this.mediaAltMatchesVariant(item.dataset.mediaAlt, variant);
          toggleItem(item, matches);
        });

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('li[data-target]').forEach((item) => {
            const mediaId = item.dataset.target.split('-').pop();
            const isFeatured = featuredMediaId && mediaId === featuredMediaId;
            const matches = isFeatured || this.mediaAltMatchesVariant(item.dataset.mediaAlt, variant);
            toggleItem(item, matches);
          });
        }

        const visibleCount = Array.from(viewerItems).filter((item) => !item.classList.contains('hidden')).length;
        const counterTotal = this.elements.viewer?.querySelector('.slider-counter--total');
        if (counterTotal) counterTotal.textContent = visibleCount;

        this.updateThumbnailArrowsVisibility(visibleCount);
        this.onGalleryUpdated();
      }

      updateThumbnailArrowsVisibility(visibleCount) {
        const thumbnails = this.elements.thumbnails;
        if (!thumbnails) return;

        const count =
          visibleCount ?? thumbnails.querySelectorAll('li[data-target]:not(.hidden)').length;

        thumbnails.classList.toggle('thumbnail-slider--hide-arrows', count <= 5);
      }

      activateFeaturedMedia(variant, prepend = true) {
        if (!variant) return;

        const sectionId = this.closest('product-info')?.dataset.section;
        const featuredMediaId = variant.featured_media?.id;

        if (featuredMediaId && sectionId) {
          this.setActiveMedia(`${sectionId}-${featuredMediaId}`, prepend);
          return;
        }

        const firstVisible = this.elements.viewer?.querySelector('li[data-media-id]:not(.hidden)');
        if (firstVisible) {
          this.setActiveMedia(firstVisible.dataset.mediaId, prepend);
        }
      }

      onGalleryUpdated() {
        this.scheduleThumbnailSliderRefresh();
      }

      scheduleThumbnailSliderRefresh() {
        const thumbnails = this.elements.thumbnails;
        if (!thumbnails) return;

        const refresh = () => {
          thumbnails.resetPages?.();
          this.elements.viewer?.resetPages?.();
          this.updateGalleryViewerHeight();
          this.updateThumbnailArrowsVisibility();
        };

        const images = thumbnails.querySelectorAll('.thumbnail-list__item:not(.hidden) img');
        const pending = Array.from(images).filter((img) => !img.complete);

        if (!pending.length) {
          requestAnimationFrame(() => requestAnimationFrame(refresh));
          return;
        }

        let remaining = pending.length;
        const done = () => {
          remaining -= 1;
          if (remaining <= 0) requestAnimationFrame(refresh);
        };

        pending.forEach((img) => {
          img.addEventListener('load', done, { once: true });
          img.addEventListener('error', done, { once: true });
        });
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const { viewer, thumbnails } = this.elements;
        if (!viewer) return;

        const activeMedia =
          viewer.querySelector(`[data-media-id="${mediaId}"]:not(.hidden)`) ||
          viewer.querySelector('[data-media-id]:not(.hidden)');
        if (!activeMedia) {
          return;
        }

        viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (thumbnails) {
            const activeThumbnail = thumbnails.querySelector(`[data-target="${mediaId}"]:not(.hidden)`);
            if (activeThumbnail) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          viewer.resetPages?.();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.updateGalleryViewerHeight();

        if (!thumbnails) return;
        const activeThumbnail = thumbnails.querySelector(`[data-target="${mediaId}"]:not(.hidden)`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        if (this.elements.thumbnails.isVertical) {
          this.elements.thumbnails.slider.scrollTo({ top: thumbnail.offsetTop });
        } else {
          this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        const { viewer } = this.elements;
        if (!viewer?.slider) return;
        viewer.slider.setAttribute('role', 'presentation');
        viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
